import { TFunction } from 'i18next'

import { clearFundingCurrencySelection } from '@pages/Traders-Room/shared/hooks/useFundingCurrencySelector'

import { santizeBase64 } from '@utils/base64Helper'
import { validateField } from '@utils/formValidation'
import { snakeToCamel } from '@utils/format.utils'

import type { BankAccount, BankAccountCreate } from '@model/BankAccount'
import type { PaymentProvider } from '@model/PaymentProviderDto'
import type { WalletDto } from '@model/WalletDto'
import { FormItemDto } from '@model/WalletPaymentProviderParametersDto'

import type { WithdrawalFormValues, WithdrawalTarget, WithdrawalWallet } from './types'

export const RESERVED_WITHDRAWAL_STEPS = ['amount', 'two-factor', 'transaction'] as const

export const isRealWithdrawalPspId = (pspId?: string): pspId is string => {
  if (!pspId) return false
  return !(RESERVED_WITHDRAWAL_STEPS as readonly string[]).includes(pspId)
}

export const getWithdrawalBasePath = (
  target: WithdrawalTarget,
  id: string,
  dashboardContext: 'traders-room' | 'payment-agent' | string = 'traders-room',
  pspId?: string
) => {
  const base = `/dashboard/${dashboardContext}/withdrawal/${target}/${id}`
  return pspId ? `${base}/${pspId}` : base
}

export const buildFieldsFromForm = (form: PaymentProvider['parameters']['form']) => {
  return Object.fromEntries(
    (form ?? [])
      .filter((f) => !!f.field)
      .map((f) => {
        const key = f.grouping ? `${f.grouping}_${f.field}` : f.field!
        return [
          snakeToCamel(key),
          Array.isArray(f.value) ? f.value[0]?.value ?? '' : String(f.value ?? ''),
        ]
      })
  )
}

export const buildWithdrawalBankAccountFields = (
  account: BankAccount
): WithdrawalFormValues['fields'] => ({
  clientBankAccountDetailsId: account.id,
  bankName: account.bankName,
  bankCode: account.bankCode,
  bankCodeSort: account.bankCodeSort,
  bankAddress: account.bankAddress,
  accountName: account.accountName,
  externalAccount: account.bankAccount,
})

export const buildInlineWithdrawalBankAccountPayload = (
  values: WithdrawalFormValues
): BankAccountCreate => {
  const { bankCodeSort, externalAccount, ...rest } = values.fields
  const { bankDepositProof } = values

  return {
    ...rest,
    bankAccount: externalAccount,
    codeSort: bankCodeSort,
    document: bankDepositProof?.base64Content
      ? {
          file: santizeBase64(bankDepositProof.base64Content),
          fileName: bankDepositProof.fileName || '',
        }
      : undefined,
  } as BankAccountCreate
}

export const buildWithdrawalInitialValues = (params: {
  destination: WalletDto
  paymentProvider: PaymentProvider
  dashboardContext: 'traders-room' | 'payment-agent' | 'introducing-broker'
}): WithdrawalFormValues => {
  const { destination, paymentProvider, dashboardContext } = params

  const wallet: WithdrawalWallet = {
    id: destination.id,
    name: destination.name,
    currency: { id: destination.currency.id, name: destination.currency.name },
    walletType: { id: destination.walletType.id, name: destination.walletType.name },
    balance: destination.balance,
    restrictions: destination.restrictions ?? [],
  }

  return {
    dashboardContext,
    wallet,
    paymentProvider,
    amount: undefined,
    comment: '',
    fields: clearFundingCurrencySelection(
      buildFieldsFromForm(paymentProvider.parameters.form),
      paymentProvider
    ),
    bankAccount: undefined,
    bankDepositProof: undefined,
    hasBankAccounts: undefined,
    terms: {
      paymentOperations: false,
      powerOfAttorney: false,
    },
  }
}

const getFieldDetailName = (field: FormItemDto): string => {
  return field.grouping ? `${field.grouping}_${field.field}` : field.field
}

export const validateWithdrawalProviderFields = (
  form: FormItemDto[] = [],
  values: Record<string, unknown> = {},
  t: TFunction
): Record<string, string> => {
  return form.reduce<Record<string, string>>((errors, field) => {
    if (!field.clientFilled) {
      return errors
    }

    const fieldName = snakeToCamel(getFieldDetailName(field))
    const fieldValue = String(values[fieldName] ?? '')
    const error = validateField(t, field.mandatory, field.validationRegex)(fieldValue)

    if (error) {
      errors[fieldName] = error
    }

    return errors
  }, {})
}
